import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { BootstrapModal } from 'shared-components/modal/bootstrap-modal';
import { injectIntl, FormattedMessage, intlShape } from 'react-intl';
import TextField from 'shared-components/reduxForms/fields/TextField/TextField';
import validate from 'validate.js';
import { icsValidation } from './utilities';
import Button from '../../elements-buttons/Button';
import SharedIntlProviderHOC from '../../shared-components/hocs/sharedIntlProviderHOC';

class ImportCalendarModalComponent extends Component {
  constructor (props) {
    super(props);
    this.state = {
      calendarName: '',
      calendarUrl: '',
      error: {
        calendarName: '',
        calendarUrl: ''
      },
      disableButton: false
    };
    this.onInputChange = this.onInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.inputFieldsInvalid = this.inputFieldsInvalid.bind(this);
    this.renderModalChoices = this.renderModalChoices.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  onInputChange (e) {
    const {name, value} = e?.target || {};
    if (!name) return;
    const error = {...this.state.error};
    error[name] = '';
    return this.setState(prevState => ({
      ...prevState,
      [name]: value,
      error
    }));
  }

  onBlur (e) {
    const { intl } = this.props;
    const textField = e.target.name;
    const value = e.target.value;
    if (this.state[textField].length <= 1) {
      const errorText = (textField === 'calendarName') ?
        intl.formatMessage({ id: 'hosting.calendar_main.import_calendar_name_error', defaultMessage: 'Please enter a name.' }) :
        intl.formatMessage({ id: 'hosting.calendar_main.import_calendar_url_error', defaultMessage: 'Please enter a calendar URL' });
      const error = {...this.state.error};
      error[textField] = errorText;
      return this.setState(prevState => ({
        ...prevState,
        error
      }));
    }
    const validateUrl = validate.single(value, { url: true });
    const errorText = intl.formatMessage({ id: 'hosting.calendar_main.import_valid_calendar_url_error', defaultMessage: 'Please enter a valid iCal URL' });
    if (!icsValidation(value, textField)) {
      return this.setState({ error: { calendarUrl: errorText } });
    }
    // Workaround fix for current issue with importing an ICS file from another Peerspace calendar
    if ((textField === 'calendarUrl') && (undefined === validateUrl) && (value.includes('peerspaceapp.com') || value.includes('peerspace.com'))) {
      const { calendar } = this.props;
      analyticsIntegrations.calendarSync('cal sync blocked', calendar.selectedSpace);
      return this.setState({ error: { calendarUrl: errorText }});
    }
    // End Workaround fix
  }

  inputFieldsInvalid () {
    const { calendarName, calendarUrl } = this.state;
    const keys = Object.keys(this.state.error);
    return (keys.some(key => this.state.error[key] !== '') || (calendarName.length < 2 || calendarUrl === ''));
  }

  handleSubmit (e) {
    const { intl } = this.props;
    const { calendarUrl, calendarName, error } = this.state;
    if (error.calendarUrl || error.calendarName) return;
    this.setState({ disableButton: true });

    this.props.importICSCalendar(this.props.calendar.selectedSpace, calendarUrl, calendarName, (res) => {
      this.setState({ disableButton: false });
      if (res.error) {
        const zendeskLocale = SharedIntlProviderHOC.getZendeskLocale();
        const errorText = (
          <FormattedMessage
            id="hosting.calendar_main.invalid_calendar_url_error"
            defaultMessage="Calendar import failed. Make sure your external calendar address is in iCalendar (iCal or ics) format and the URL is copied correctly. {link}"
            values={{
              link:
              <a
                rel="noreferrer noopener"
                href={`https://support.peerspace.com/hc/${zendeskLocale}/articles/360015812591-Can-I-sync-a-calendar-with-Peerspace-`}
                target="_blank"
              >
                <FormattedMessage id="hosting.calendar_main.ical_learn_more" defaultMessage="Learn More"/>
              </a>
            }}
          />);
        return this.setState({ error: { calendarUrl: errorText } });
        // handle error condition Ex. if url is invalid, the invalid property will show: res.invalid: ['source_url']
      }
      this.props.syncCronofyCalendar(calendarName);
    });
  }

  renderModalChoices (surveyComplete) {
    const { toggleModal } = this.props;
    if (surveyComplete) {
      return (
        <div className="text-center">
          <button className="btn btn-no-outline" onClick={()=>toggleModal('ConnectExternalCalendar') }>
            <a>
              <FormattedMessage
                id="hosting.calendar_main.import_calendar_modal_back_link"
                defaultMessage="Back to sync external calendar"
              />
            </a>
          </button>
        </div>
      );
    }
    return (
      <div className="flex column middle">
        <button className="btn btn-no-outline" onClick={()=>toggleModal('WhyCantSyncModal') }>
          <a>
            <FormattedMessage
              id="hosting.calendar_main.not_now"
              defaultMessage="Not now"
            />
          </a>
        </button>
        <button className="btn btn-no-outline" onClick={()=>toggleModal('ConnectExternalCalendar') }>
          <a>
            <FormattedMessage
              id="hosting.calendar_main.ical_not_supported"
              defaultMessage="My calendar doesn't support iCal"
            />
          </a>
        </button>
      </div>
    );
  }

  closeModal () {
    this.props.clearHashUrl();
    this.props.toggleModal('');
  }

  render () {
    const invalidInput = this.inputFieldsInvalid();
    const { intl, showModal, changeModal, toggleModal, surveyComplete } = this.props;
    const { calendarName, calendarUrl, error } = this.state;
    const modalBody = (
      <div className="row">
        <div className="col-xs-12">
          <h3 className="text-center no-margin">
            <FormattedMessage id="hosting.calendar_main.import_calendar_modal_heading" defaultMessage="Import calendar" />
          </h3>
          <p className="text-center margin-lg-top margin-lg-bottom">
            <FormattedMessage
              id="hosting.calendar_main.import_calendar_modal_text"
              defaultMessage="Copy and paste the export URL provided by your external calendar. URL must be in iCalendar (iCal or ics) format"
            />
          </p>
          <div className="row">
            <div className="col-xs-12">
              <label htmlFor='calendarName' className="margin-lg-top margin-md-bottom">
                <FormattedMessage id="hosting.calendar_main.import_calendar_modal_calendar_label" defaultMessage="Calendar name" />
              </label>
              <TextField
                errorMessage={error.calendarName}
                attributes={{ name: 'calendarName', value: calendarName, onChange: this.onInputChange, onBlur: this.onBlur }}
                meta={{ error: !!error.calendarName, submitFailed: !!error.calendarName }}
                placeholder={intl.formatMessage({ id: 'hosting.calendar_main.calendar_name_placeholder', defaultMessage: 'Airbnb Calendar' })}
              />
              <label htmlFor='calendarUrl' className="margin-lg-top margin-md-bottom text-transform-none">
                <FormattedMessage id="hosting.calendar_main.import_calendar_modal_calendar_address" defaultMessage="Calendar address (URL)" />
              </label>
              <TextField
                errorMessage={error.calendarUrl}
                attributes={{ name: 'calendarUrl', value: calendarUrl, onChange: this.onInputChange, onBlur: this.onBlur, maxLength: 400 }}
                meta={{ error: !!error.calendarUrl, submitFailed: !!error.calendarUrl }}
                placeholder={intl.formatMessage({ id: 'hosting.calendar_main.calendar_ics_address_placeholder', defaultMessage: 'https://airbnb.com/calendar/ical/415650.ics' })}
              />
            </div>
          </div>
          <Button
            category="primary"
            onClick={this.handleSubmit}
            isLoading={this.state.disableButton}
            disabled={this.state.disableButton || invalidInput}
            className="btn btn-primary btn-block margin-lg-top margin-xl-bottom"
          >
            <FormattedMessage
              id="hosting.calendar_main.import_calendar_modal_calendar_cta"
              defaultMessage="Import calendar"
            />
          </Button>
          {this.renderModalChoices(surveyComplete)}
        </div>
      </div>
    );
    return (
      <BootstrapModal
        showModal={showModal}
        body={modalBody}
        onClose={this.closeModal}
      />
    );
  }
}

export const ImportCalendarModal = injectIntl(ImportCalendarModalComponent);

ImportCalendarModalComponent.propTypes = {
  selectedSpace: PropTypes.string,
  displayOptionsOnSyncModal: PropTypes.bool,
  showModal: PropTypes.bool,
  toggleModal: PropTypes.bool,
  changeModal: PropTypes.func,
  importICSCalendar: PropTypes.func,
  syncCronofyCalendar: PropTypes.func,
  clearHashUrl: PropTypes.func
};
